import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { useApi } from "../utils/Api";
import { usePersistentForm } from "../hooks/usePersistentForm";
import ErrorModal from "../components/ErrorModal";
import { useErrorModal } from "../hooks/useErrorModal";
import SEO from "../components/SEO";

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

const getTodayUK = () => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  return formatter.format(new Date());
};

const FORM_DEFAULTS = {
  fullName: "",
  email: "",
  phone: "",
  eventType: "Wedding",
  eventDate: "",
  location: "",
  startTime: "",
  coverageNotes: "",
};

export default function EventPackages() {
  const { apiFetch } = useApi();
  const formRef = useRef(null);
  const { errorMessage, isErrorOpen, showError, closeError } = useErrorModal();

  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [packagesError, setPackagesError] = useState("");
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [packageType, setPackageType] = useState(() => {
    return localStorage.getItem("kinews_event_tab") || "wedding";
  });

  const { form, updateField } = usePersistentForm({
    scope: "public-site",
    formId: "wedding-package-booking-details",
    version: "v1",
    initialValues: FORM_DEFAULTS,
    persistFields: ["fullName", "email", "phone"],
    ttlMs: 1000 * 60 * 60 * 24 * 45,
    debounceMs: 300,
  });

  useEffect(() => {
    localStorage.setItem("kinews_event_tab", packageType);
    
    // Ensure form state matches the loaded tab
    if (packageType === "general") {
      updateField("eventType", "");
    } else if (packageType === "wedding") {
      updateField("eventType", "Wedding");
    }
  }, [packageType, updateField]);

  useEffect(() => {
    const resetSubmittingState = () => {
      setSubmitting(false);
    };

    const handlePageShow = () => {
      resetSubmittingState();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        resetSubmittingState();
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const minUkDate = useMemo(() => getTodayUK(), []);

  const ensureCoverageText = (value) => {
    if (!value) return "";

    const original = String(value).trim();
    const lower = original.toLowerCase();

    if (lower.includes("coverage")) return original;

    return `${original} coverage`;
  };

  const loadPackages = useCallback(async () => {
    try {
      setLoadingPackages(true);
      setPackagesError("");

      const endpoint = packageType === "general" ? "get_event_packages" : "get_wedding_packages";
      const res = await apiFetch(endpoint);

      if (!res?.success) {
        throw new Error(res?.message || "Failed to load packages.");
      }

      const mappedPackages = (Array.isArray(res.data) ? res.data : []).map(
        (item) => ({
          id: Number(item.id),
          name: item.name,
          priceFrom: Number(item.price),
          duration: ensureCoverageText(item.coverage),
          highlights: Array.isArray(item.features) ? item.features : [],
        }),
      );

      setPackages(mappedPackages);
      setSelectedPackageId((prev) =>
        prev && mappedPackages.some((pkg) => pkg.id === Number(prev))
          ? Number(prev)
          : mappedPackages.length
            ? mappedPackages[0].id
            : null,
      );
    } catch (err) {
      setPackagesError(err.message || "Unable to load packages.");
      setPackages([]);
      setSelectedPackageId(null);
    } finally {
      setLoadingPackages(false);
    }
  }, [apiFetch, packageType]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const selectedPackage = useMemo(() => {
    return (
      packages.find((p) => Number(p.id) === Number(selectedPackageId)) ||
      packages[0] ||
      null
    );
  }, [packages, selectedPackageId]);

  const onChange = (key) => (e) => {
    updateField(key, e.target.value);

    if (isErrorOpen) {
      closeError();
    }
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onSelectPackage = (id) => {
    setSelectedPackageId(id);

    if (isErrorOpen) {
      closeError();
    }

    requestAnimationFrame(scrollToForm);
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!selectedPackage) {
      showError("Please select a package.");
      return;
    }

    if (!form.fullName.trim()) {
      showError("Please enter your full name.");
      return;
    }

    if (!form.email.trim()) {
      showError("Please enter your email address.");
      return;
    }

    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) {
      showError("Please enter a valid email address.");
      return;
    }

    if (!form.phone.trim()) {
      showError("Please enter your phone number.");
      return;
    }

    if (!form.eventDate) {
      showError("Please choose an event date.");
      return;
    }

    if (!form.location.trim()) {
      showError("Please enter the event location.");
      return;
    }

    if (!form.startTime.trim()) {
      showError("Please enter the event start time.");
      return;
    }

    if (submitting) return;

    try {
      setSubmitting(true);
      closeError();

      const payload = {
        packageId: selectedPackage.id,
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        eventType: form.eventType.trim(),
        eventDate: form.eventDate,
        location: form.location.trim(),
        startTime: form.startTime.trim(),
        coverageNotes: form.coverageNotes.trim(),
        packageType: packageType,
      };

      const endpoint = "create_event_checkout_session";
      const res = await apiFetch(endpoint, payload);

      if (!res?.success) {
        throw new Error(res?.message || "Unable to start payment.");
      }

      if (!res?.checkoutUrl) {
        throw new Error("Stripe checkout link was not returned.");
      }

      window.location.href = res.checkoutUrl;
    } catch (err) {
      showError(err.message || "Unable to start payment.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Event Photography Packages | Kinews Studio Limited"
        description="Explore our professional event photography packages for weddings and special occasions. Capture every moment with Kinews Studio Limited."
        url={window.location.href}
      />
      <main className="event-packages-page min-h-screen bg-[#0b0b0c] text-white">
        <style>{`
          input[type="date"]::-webkit-calendar-picker-indicator,
          input[type="time"]::-webkit-calendar-picker-indicator {
            opacity: 0;
            position: absolute;
            right: 0;
            width: 100%;
            height: 100%;
            cursor: pointer;
          }

          input[type="date"],
          input[type="time"] {
            color-scheme: dark;
          }
        `}</style>

        <section className="relative overflow-hidden bg-[#050505] text-white">
          {/* Lighting Effects */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -left-[10%] -top-[10%] h-[40rem] w-[40rem] rounded-full bg-pink-500/15 blur-[120px]" />
            <div className="absolute -right-[10%] top-[20%] h-[35rem] w-[35rem] rounded-full bg-pink-600/10 blur-[100px]" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[50rem] w-[50rem] rounded-full bg-pink-500/5 blur-[150px]" />
          </div>

          <div className="relative mx-auto max-w-7xl px-5 pb-14 pt-28 md:pb-24 md:pt-40">
            <h1 className="mt-3 text-4xl font-bold tracking-tighter md:text-5xl lg:text-6xl">
              Event Packages
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/60 md:text-xl">
              Professional coverage for weddings, parties, and special occasions.
            </p>

            <div className="mt-10 flex p-1.5 bg-white/5 rounded-2xl border border-white/10 w-fit shadow-xl shadow-black/50 backdrop-blur-md">
              <button
                type="button"
                onClick={() => {
                  setPackageType("wedding");
                  updateField("eventType", "Wedding");
                }}
                className={`px-8 py-3 rounded-xl font-medium text-base transition-all duration-300 ${
                  packageType === "wedding" 
                    ? "bg-pink-500 text-white shadow-lg shadow-pink-500/25" 
                    : "text-white/60 hover:text-white hover:bg-white/10"
                }`}
              >
                Weddings
              </button>
              <button
                type="button"
                onClick={() => {
                  setPackageType("general");
                  updateField("eventType", "");
                }}
                className={`px-8 py-3 rounded-xl font-medium text-base transition-all duration-300 ${
                  packageType === "general" 
                    ? "bg-pink-500 text-white shadow-lg shadow-pink-500/25" 
                    : "text-white/60 hover:text-white hover:bg-white/10"
                }`}
              >
                General Events
              </button>
            </div>
          </div>
        </section>

        <section className="bg-[#0f1012]">
          <div className="mx-auto max-w-7xl px-5 py-16">
            <h2 className="text-3xl font-semibold">
              {packageType === "wedding" ? "Wedding Packages" : "General Event Packages"}
            </h2>

            {loadingPackages ? (
              <div className="mt-8 rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-white/70">
                Loading packages...
              </div>
            ) : packagesError ? (
              <div className="mt-8 rounded-3xl border border-red-500/20 bg-red-500/10 p-8 text-red-200">
                {packagesError}
              </div>
            ) : packages.length === 0 ? (
              <div className="mt-8 rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-white/70">
                No packages are available right now.
              </div>
            ) : (
              <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
                {packages.map((p) => {
                  const active = Number(p.id) === Number(selectedPackageId);

                  return (
                    <div
                      key={p.id}
                      className={[
                        "flex h-full flex-col rounded-3xl border p-6 transition",
                        active
                          ? "border-pink-500 bg-gradient-to-b from-pink-500/20 to-white/5 shadow-lg"
                          : "border-white/10 bg-white/[0.03] hover:-translate-y-1 hover:border-white/20",
                      ].join(" ")}
                    >
                      <div className="flex h-full flex-col">
                        <div>
                          <div className="flex items-start justify-between">
                            <h3 className="text-xl font-semibold">{p.name}</h3>
                          </div>

                          <p className="mt-3 text-sm text-white/70">
                            {p.duration}
                          </p>

                          <p className="mt-4 text-3xl font-bold text-pink-400">
                            {money(p.priceFrom)}
                          </p>

                          <ul className="mb-6 mt-5 space-y-2 text-sm text-white/70">
                            {p.highlights.map((x, index) => (
                              <li
                                key={`${p.id}-${index}-${x}`}
                                className="flex gap-2"
                              >
                                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-pink-400" />
                                {x}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <button
                          type="button"
                          onClick={() => onSelectPackage(p.id)}
                          className="mt-auto w-full rounded-xl bg-pink-500 py-3 font-semibold transition hover:bg-pink-600"
                        >
                          {active ? "Selected" : "Select"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section className="border-y border-white/10 bg-[#0b0b0c]">
          <div className="mx-auto max-w-5xl px-5 py-10 text-center">
            <p className="text-lg text-white/80">
              All digital images are high resolution with print release licence
            </p>
          </div>
        </section>

        <section ref={formRef} className="bg-[#0f1012]">
          <div className="mx-auto max-w-7xl px-5 py-16">
            <h2 className="text-3xl font-semibold">Book your package</h2>

            <div className="mt-8 grid gap-8 lg:grid-cols-12">
              <form
                id="wedding-package-form"
                noValidate
                onSubmit={onSubmit}
                className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 lg:col-span-7"
              >
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-white/70">Full name</label>
                    <input
                      className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none focus:border-pink-500"
                      value={form.fullName}
                      onChange={onChange("fullName")}
                      required
                      autoComplete="name"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-white/70">Email address</label>
                    <input
                      type="email"
                      className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none focus:border-pink-500"
                      value={form.email}
                      onChange={onChange("email")}
                      required
                      autoComplete="email"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-white/70">Phone number</label>
                    <input
                      className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none focus:border-pink-500"
                      value={form.phone}
                      onChange={onChange("phone")}
                      required
                      autoComplete="tel"
                    />
                  </div>
                </div>

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-white/70">Event date</label>
                    <div className="relative min-w-0">
                      <input
                        type="date"
                        min={minUkDate}
                        className="w-full min-w-0 appearance-none rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none focus:border-pink-500"
                        value={form.eventDate}
                        onChange={onChange("eventDate")}
                        required
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-white/70">Location</label>
                    <input
                      className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none focus:border-pink-500"
                      value={form.location}
                      onChange={onChange("location")}
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-white/70">Event type (occasion)</label>
                    <input
                      className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none focus:border-pink-500"
                      value={form.eventType}
                      onChange={onChange("eventType")}
                      required
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-sm font-medium text-white/70">Event time</label>
                    <div className="relative min-w-0">
                      <input
                        type="time"
                        className="w-full min-w-0 appearance-none rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none focus:border-pink-500"
                        value={form.startTime}
                        onChange={onChange("startTime")}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-white/70">Additional notes</label>
                  <textarea
                    className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none focus:border-pink-500"
                    rows={4}
                    value={form.coverageNotes}
                    onChange={onChange("coverageNotes")}
                  />
                </div>
              </form>

              <aside className="lg:col-span-5">
                <div className="sticky top-6 rounded-3xl border border-pink-500/20 bg-gradient-to-b from-[#181116] to-[#0d0b0d] p-7 shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
                  <p className="text-sm uppercase tracking-[0.25em] text-pink-400/80">
                    Checkout
                  </p>
                  <h3 className="mt-2 text-2xl font-semibold">Order Summary</h3>

                  {!selectedPackage ? (
                    <div className="mt-8 text-sm text-white/70">
                      No package selected.
                    </div>
                  ) : (
                    <div className="mt-8 space-y-4 text-sm text-white/75">
                      <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                        <span>Package</span>
                        <span className="text-right font-medium text-white">
                          {selectedPackage.name}
                        </span>
                      </div>

                      <div className="flex justify-between gap-4 border-b border-white/10 pb-3">
                        <span>Coverage</span>
                        <span className="text-right font-medium text-white">
                          {selectedPackage.duration}
                        </span>
                      </div>

                      <div className="border-b border-white/10 pb-3">
                        <span className="mb-3 block">Package Features</span>

                        {selectedPackage.highlights?.length ? (
                          <ul className="space-y-2">
                            {selectedPackage.highlights.map(
                              (feature, index) => (
                                <li
                                  key={`summary-feature-${selectedPackage.id}-${index}`}
                                  className="flex items-start gap-2 text-white/85"
                                >
                                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-pink-400" />
                                  <span>{feature}</span>
                                </li>
                              ),
                            )}
                          </ul>
                        ) : (
                          <span className="text-white/60">
                            No package features listed.
                          </span>
                        )}
                      </div>

                      <div className="mt-6 rounded-2xl bg-white/5 px-4 py-4">
                        <div className="flex items-center justify-between">
                          <span className="text-base font-semibold text-white">
                            Total
                          </span>
                          <span className="text-2xl font-semibold text-pink-400">
                            {money(selectedPackage.priceFrom)}
                          </span>
                        </div>
                      </div>

                      <button
                        type="submit"
                        form="wedding-package-form"
                        disabled={submitting || loadingPackages}
                        className={`mt-6 w-full rounded-xl py-4 font-semibold transition ${
                          submitting || loadingPackages
                            ? "cursor-not-allowed bg-pink-500/40 text-white/70"
                            : "bg-pink-500 text-white hover:bg-pink-600"
                        }`}
                      >
                        {submitting ? "Redirecting..." : "Continue to Payment"}
                      </button>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="bg-black text-white">
          <div className="mx-auto max-w-7xl px-5 py-14">
            <div className="flex flex-col items-center justify-between gap-6 rounded-3xl border border-white/10 p-8 md:flex-row">
              <div>
                <h2 className="text-2xl font-semibold">
                  Need a studio session instead?
                </h2>
                <p className="mt-2 text-white/70">
                  Book portraits and indoor shoots easily.
                </p>
              </div>

              <Link
                to="/book"
                className="rounded-xl bg-pink-500 px-6 py-3 font-semibold text-white"
              >
                Go to Bookings
              </Link>
            </div>
          </div>
        </section>
      </main>

      <ErrorModal
        open={isErrorOpen}
        message={errorMessage}
        onClose={closeError}
        autoCloseMs={4000}
      />
    </>
  );
}
